<template>
  <form name="form" v-on:submit.prevent="submit" v-on:change="changeHandler">
    <fieldset
      :disabled="readonly"
      style="border:0;padding:0;margin:0;min-width:0;"
    >
      <div class="card-body">
        <div class="text-right" v-if="!readonly">
          <form-button :indicator="indicator" v-on:clicked="submit"
            >Save
          </form-button>
        </div>
        <div class="clearfix"></div>
        <hr />

        <div class="row">
          <div class="col-lg-6">
            <liability-type
              :incident="incident"
              :type="liabilityTypeId"
              :show-error="submitted && !liabilityTypeId"
            ></liability-type>

            <selectlist
              :options="liabilityStatuses"
              v-model="details.liability_status_id"
              label="Liability Status"
              helper="Please select liability status"
              name="liability_status_id"
              :readonly="readonly"
            ></selectlist>

            <selectlist
              :options="categories"
              v-model="details.category_id"
              label="Incident Category"
              helper="Please select the type of incident"
              name="incident_type"
              label-name="name"
              placeholder="---Please Select---"
              :readonly="readonly"
            ></selectlist>

            <input-block
              v-model="details.description"
              :readonly="readonly"
              name="details"
              label="Accident Details"
              helper="Please enter the details of the accident here, being as descriptive as possible."
            ></input-block>

            <input-block
              v-model="details.further_details"
              :readonly="readonly"
              name="further_details"
              label="Any Further Details"
              helper="Are there any further details, not directly related to the accident you would like to note?"
            ></input-block>
          </div>

          <div class="col-lg-6">
            <accident-location
              v-on:located="setLocation"
              :location="details.location"
              :lat="details.latitude"
              :lng="details.longitude"
              v-if="loaded"
            ></accident-location>
          </div>

          <div class="col-lg-12">
            <fieldset>
              <legend>
                Journey Details
                <small class="text-muted">
                  - Enter the journey route and what the vehicle was being used
                  for here
                </small>
              </legend>

              <journey-details
                ref="journeyDetails"
                v-on:set-reason="setReason"
                v-on:set-route="setJourney"
              ></journey-details>
            </fieldset>
          </div>

          <div class="col-lg-6">
            <fieldset>
              <legend>
                CCTV Details
                <small class="text-muted">
                  - Enter the cctv footage details.
                </small>
              </legend>
              <toggleswitch
                :labels="true"
                name="cctv_footage_available"
                label="CCTV Footage"
                v-model="details.cctv_footage_available"
                helper="Is CCTV footage available?"
              ></toggleswitch>
              <toggleswitch
                :labels="true"
                name="dashcam_footage_available"
                label="Dashcam Footage"
                v-model="details.dashcam_footage_available"
                helper="Is Dashcam footage available?"
              ></toggleswitch>
            </fieldset>

            <fieldset>
              <legend>
                Client Wearing
                <small class="text-muted">
                  - Enter the client wearing details.
                </small>
              </legend>
              <toggleswitch
                :labels="true"
                name="seat_belt"
                label="Seat Belt"
                v-model="details.seat_belt"
                helper="Is the client wearing seat belt?"
              ></toggleswitch>
              <toggleswitch
                :labels="true"
                name="helmet"
                label="Helmet"
                v-model="details.helmet"
                helper="Is the client wearing helmet?"
              ></toggleswitch>
              <toggleswitch
                :labels="true"
                name="protective_clothing"
                label="Protective clothing"
                v-model="details.protective_clothing"
                helper="Is the client wearing protective clothing?"
              ></toggleswitch>
            </fieldset>
          </div>

          <div class="col-lg-6">
            <fieldset>
              <legend>
                Speed Details
                <small class="text-muted">
                  - Enter the speed details.
                </small>
              </legend>
              <inputbox
                v-model="details.speed_limit"
                name="speed_limit"
                label="Speed Limit"
                helper="Speed Limit"
              ></inputbox>
              <inputbox
                v-model="details.client_speed"
                name="client_speed"
                label="Client Speed"
                helper="Client Speed"
              ></inputbox>
              <inputbox
                v-model="details.third_party_speed"
                name="third_party_speed"
                label="Third party Speed"
                helper="Third party Speed"
              ></inputbox>
            </fieldset>
          </div>
        </div>
      </div>
      <div class="card-footer text-right" v-if="!readonly">
        <form-button :indicator="indicator" v-on:clicked="submit"
          >Save
        </form-button>
      </div>
    </fieldset>
  </form>
</template>

<script>
import LiabilityType from './../../Shared/LiabilityType.vue';
import AccidentLocation from './Children/AccidentLocation.vue';
import JourneyDetails from './Children/JourneyDetails.vue';

export default {
  props: {
    incident: {
      type: String,
      required: true
    },
    readonly: {
      type: Boolean,
      required: false
    }
  },

  components: {
    'liability-type': LiabilityType,
    'accident-location': AccidentLocation,
    'journey-details': JourneyDetails
  },

  data() {
    return {
      indicator: false,
      liabilityTypeId: '',
      submitted: false,
      loaded: false,
      journey: new window.Form({
        start_location: '',
        start_latitude: '',
        start_longitude: '',
        end_location: '',
        end_latitude: '',
        end_longitude: '',
        use_id: ''
      }),
      details: new window.Form({
        liability_status_id: null,
        client_at_fault: false,
        category_id: null,
        description: null,
        latitude: null,
        longitude: null,
        location: null,
        pitch: null,
        heading: null,
        further_details: null,
        cctv_footage_available: false,
        dashcam_footage_available: false,
        seat_belt: false,
        helmet: false,
        protective_clothing: false,
        speed_limit: null,
        client_speed: null,
        third_party_speed: null
      }),
      data_entered: false
    };
  },

  computed: {
    client() {
      return this.$store.getters['Incident/client'];
    },

    journeyDetails() {
      return this.$store.getters['Incident/journey'];
    },

    vehicle() {
      return this.$store.getters['Incident/clientVehicle'];
    },

    categories() {
      return this.$store.getters['IncidentCategories/all'];
    },

    liabilityStatuses() {
      return this.$store.getters['LiabilityStatuses/all'];
    },

    liabilityType() {
      return this.$store.getters['Incident/liabilityType'];
    },

    liabilityStatus() {
      return this.$store.getters['Incident/liabilityStatus'];
    }
  },

  mounted() {
    this.mount();
  },

  methods: {
    mount() {
      this.$store.commit('IncidentCategories/retrieve');
      this.$store.commit('LiabilityStatuses/retrieve');

      if (this.liabilityStatus) {
        this.details.liability_status_id = this.liabilityStatus;
      }

      if (this.client && Object.keys(this.client).length) {
        this.setDetails(this.$store.getters['Incident/details']);
      } else {
        this.loaded = true;
      }

      if (this.journeyDetails && Object.keys(this.journeyDetails).length) {
        this.setJourney(this.journeyDetails);
        this.$refs.journeyDetails.getJourney(this.journey);
      }

      if (this.liabilityType) {
        this.setLiabilityTypeId(this.liabilityType);
      }
    },

    setJourney(data) {
      this.journey.start_location = data.start_location;
      this.journey.start_latitude = data.start_latitude;
      this.journey.start_longitude = data.start_longitude;
      this.journey.end_location = data.end_location;
      this.journey.end_latitude = data.end_latitude;
      this.journey.end_longitude = data.end_longitude;
      this.journey.use_id = data.use_id;
    },

    setReason(reason) {
      this.journey.use_id = reason;
    },

    setDetails(data) {
      this.details.category_id = data.category_id;
      this.details.description = data.description;
      this.details.client_at_fault = data.client_at_fault;
      this.details.further_details = data.further_details;

      this.details.cctv_footage_available = data.cctv_footage_available;
      this.details.dashcam_footage_available = data.dashcam_footage_available;
      this.details.seat_belt = data.seat_belt;
      this.details.helmet = data.helmet;
      this.details.protective_clothing = data.protective_clothing;
      this.details.speed_limit = data.speed_limit;
      this.details.client_speed = data.client_speed;
      this.details.third_party_speed = data.third_party_speed;

      if (data.latitude && data.longitude) {
        this.details.latitude = data.latitude;
        this.details.longitude = data.longitude;
      }

      this.details.location = data.location;
      this.details.pitch = data.pitch;
      this.details.heading = data.heading;

      this.loaded = true;
    },

    setLocation(data) {
      this.details.latitude = data.map.latitude;
      this.details.longitude = data.map.longitude;
      this.details.location = data.location;
      this.details.pitch = data.map.pitch;
      this.details.heading = data.map.heading;
    },

    changeHandler() {
      if (!this.data_entered) {
        this.data_entered = true;
      }

      this.$emit('unsaved-data', this.data_entered);
    },

    setLiabilityTypeId(type) {
      this.liabilityTypeId = type ? type.identifier : '';
    },

    submit() {
      if (this.readonly) return;
      window.toastify.info('Saving', "We're saving those details for you.");

      this.submitted = true;

      if (this.liabilityTypeId) {
        this.indicator = true;

        this.details
          .post(`/incidents/${this.incident}/details`)
          .then(response => {
            this.$store.commit('Incident/setDetails', response);
            this.$store.commit(
              'Incident/setLiabilityStatus',
              this.details.liability_status_id
            );

            this.journey
              .put(
                `/incidents/${this.incident}/client/${this.client.identifier}/journey`
              )
              .then(response => {
                this.$store.commit('Incident/setJourney', response);

                this.indicator = false;
                this.data_entered = false;
                this.$emit('unsaved-data', this.data_entered);

                window.toastify.success(
                  'Saved',
                  "We've updated those details for you."
                );
              })
              .catch(error => {
                this.indicator = false;
                window.toastify.error(
                  'Error',
                  'Sorry, there was an error when saving the accident details.'
                );
              });
          })
          .catch(error => {
            this.indicator = false;
            window.toastify.error(
              'Error',
              'Sorry, there was an error when saving the accident details.'
            );
          });
      } else {
        window.toastify.error(
          'Error',
          'Sorry, there was an error when saving the accident details.'
        );
      }
    }
  },

  watch: {
    liabilityType(value) {
      this.setLiabilityTypeId(value);
    }
  }
};
</script>
